import type { BundleLink, FhirResource } from 'fhir/r4';
import type { Bundle, WithId } from '../types';

export const getNextPageToken = (
  bundleLink?: BundleLink,
): string | undefined => {
  if (!bundleLink) {
    return undefined;
  }

  const itemUrl = new URL(bundleLink.url);
  const pageToken = itemUrl.searchParams.get('_page_token');

  return pageToken || undefined;
};

export const getSelfPageToken = (
  bundleLink?: BundleLink[],
): string | undefined => {
  if (!bundleLink) {
    return undefined;
  }

  const selfLink = bundleLink.find((l) => l.relation === 'self');
  if (!selfLink) {
    return undefined;
  }

  try {
    const itemUrl = new URL(selfLink.url);
    const pageToken = itemUrl.searchParams.get('_page_token');

    if (!pageToken) {
      return undefined;
    }

    return pageToken;
  } catch (err) {
    return selfLink.url;
  }
};

export const getNextLink = (
  bundle: Bundle<WithId<FhirResource>>,
): BundleLink | undefined => bundle.link?.find((l) => l.relation === 'next');

export const getPageTokenFromBundle = (
  bundle: Bundle<WithId<FhirResource>>,
): string | undefined => {
  const foundLink = getNextLink(bundle);

  return getNextPageToken(foundLink);
};

export interface BundlePaginationTokens {
  nextToken?: string;
  previousToken?: string;
}

export const getPaginationToken = <T extends FhirResource>(
  relation: 'next' | 'previous',
  bundle: Bundle<T>,
): string | undefined => {
  const { link } = bundle;

  const found = link?.find((l) => l.relation === relation);

  if (!found) {
    return undefined;
  }

  const url = new URL(found.url);

  const pageToken = url.searchParams.get('_page_token');

  if (!pageToken) {
    return undefined;
  }

  return pageToken;
};

export const getBundlePaginationTokens = <T extends FhirResource>(
  bundle: Bundle<T>,
): BundlePaginationTokens => {
  const previousToken = getPaginationToken('previous', bundle);
  const nextToken = getPaginationToken('next', bundle);

  return {
    previousToken,
    nextToken,
  };
};
